import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import apiService from '../services/apiService';

export default function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [successData, setSuccessData] = useState(null);

  const type = searchParams.get('type'); // 'pack' or null (booking)
  const isPack = type === 'pack';

  useEffect(() => {
    const orderId = searchParams.get('order_id');

    if (!orderId) {
      setStatus('error');
      setErrorMsg('Invalid payment return URL — order ID missing.');
      return;
    }

    const verify = isPack
      ? apiService.verifyPackPayment({ orderId })
      : (() => {
          const message = sessionStorage.getItem('cf_booking_msg') || '';
          sessionStorage.removeItem('cf_booking_msg');
          return apiService.verifyPayment({ orderId, message });
        })();

    verify
      .then(res => {
        if (res.success) {
          setSuccessData(res.data);
          setStatus('success');
          if (!isPack) {
            setTimeout(() => navigate('/learner'), 3000);
          }
        } else {
          setStatus('error');
          setErrorMsg(res.message || 'Payment verification failed.');
        }
      })
      .catch(err => {
        setStatus('error');
        setErrorMsg(err.message || 'Payment verification failed.');
      });
  }, []);

  const handleGoAfterPackSuccess = () => {
    const expertId = sessionStorage.getItem('cf_pack_expert_id');
    sessionStorage.removeItem('cf_pack_expert_id');
    if (expertId) navigate(`/expert/${expertId}`);
    else navigate('/learner?tab=packs');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg2)', padding: 24 }}>
      <div className="card" style={{ maxWidth: 440, width: '100%', padding: 40, textAlign: 'center' }}>

        {status === 'verifying' && (
          <>
            <div style={{ marginBottom: 20 }}>
              <Loader size={48} style={{ color: 'var(--teal)', animation: 'spin 1s linear infinite' }} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
              {isPack ? 'Activating your pack…' : 'Confirming your payment…'}
            </h2>
            <p style={{ color: 'var(--ink3)', fontSize: 14 }}>Please wait while we verify your payment with Cashfree. Do not close this page.</p>
          </>
        )}

        {status === 'success' && isPack && (
          <>
            <div style={{ marginBottom: 20 }}>
              <CheckCircle size={56} style={{ color: 'var(--teal)' }} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: 'var(--teal)' }}>Pack Activated!</h2>
            <p style={{ color: 'var(--ink2)', fontSize: 14, marginBottom: 6 }}>
              Your payment was successful.
            </p>
            {successData && (
              <div style={{ background: 'var(--teal-lt, #e6f7f5)', border: '1px solid var(--teal)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--ink2)' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>🎉 {successData.sessions_total} sessions activated</div>
                <div style={{ color: 'var(--ink3)' }}>Valid until {new Date(successData.expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
              </div>
            )}
            <button onClick={handleGoAfterPackSuccess} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 13 }}>
              Book a Session Now →
            </button>
            <button onClick={() => navigate('/learner?tab=packs')} className="btn" style={{ width: '100%', justifyContent: 'center', padding: 12, marginTop: 10 }}>
              View My Packs
            </button>
          </>
        )}

        {status === 'success' && !isPack && (
          <>
            <div style={{ marginBottom: 20 }}>
              <CheckCircle size={56} style={{ color: 'var(--teal)' }} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: 'var(--teal)' }}>Booking Confirmed!</h2>
            <p style={{ color: 'var(--ink2)', fontSize: 14, marginBottom: 20 }}>Your payment was successful and the booking has been sent to the expert for confirmation. You'll be notified once they accept.</p>
            <p style={{ color: 'var(--ink4)', fontSize: 12 }}>Redirecting to your bookings in 3 seconds…</p>
            <button onClick={() => navigate('/learner')} className="btn btn-primary" style={{ marginTop: 20, width: '100%', justifyContent: 'center', padding: 13 }}>
              Go to My Bookings →
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ marginBottom: 20 }}>
              <XCircle size={56} style={{ color: '#ef4444' }} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: '#ef4444' }}>
              {isPack ? 'Pack Purchase Failed' : 'Booking Failed'}
            </h2>
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 14px', marginBottom: 20, fontSize: 13, color: '#991b1b', lineHeight: 1.6 }}>
              {errorMsg}
            </div>
            {(errorMsg.toLowerCase().includes('slot') || isPack) && (
              <div style={{ background: 'var(--teal-lt)', border: '1px solid var(--teal)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: 'var(--ink2)' }}>
                <strong style={{ color: 'var(--teal)' }}>Payment charged?</strong> If Cashfree charged you, the amount will be automatically refunded within 5–7 business days.
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
              <button onClick={() => navigate(-1)} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }}>
                ← Go Back
              </button>
              <button onClick={() => navigate('/')} className="btn" style={{ width: '100%', justifyContent: 'center', padding: 12 }}>
                Back to Home
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
